/**
 * Privacy-region gating for analytics.
 *
 * Product analytics (no-PII, first-party, single-domain) runs on a
 * legitimate-interest basis for most of the world — but the EU/EEA, UK, and
 * Switzerland require PRIOR opt-in consent before a non-essential cookie is set
 * (ePrivacy) and before analytics processing begins (GDPR / UK GDPR / FADP). For
 * visitors in those regions we fall back to the explicit-opt-in behaviour: the
 * PostHog client is not even initialised until the user grants "analytics"
 * consent, so no cookie is written and no event fires.
 *
 * The country is resolved server-side from Cloudflare edge geo and exposed to the
 * client via `/api/privacy-region` (the page HTML stays geo-free so it remains
 * edge-cacheable — same reason `/api/market` exists). When the country is unknown
 * (local dev, non-CF runtime, geo lookup failure) we DEFAULT TO REQUIRING CONSENT
 * — fail closed, never capture without consent when we can't prove the region is
 * outside the strict-consent zone.
 */

/**
 * ISO-3166-1 alpha-2 codes where prior opt-in consent is legally required before
 * analytics cookies/processing: EU-27 + EEA (Iceland, Liechtenstein, Norway) +
 * the UK + Switzerland (FADP, GDPR-aligned).
 */
export const PRIOR_CONSENT_COUNTRIES: ReadonlySet<string> = new Set([
	// EU-27
	"AT",
	"BE",
	"BG",
	"HR",
	"CY",
	"CZ",
	"DK",
	"EE",
	"FI",
	"FR",
	"DE",
	"GR",
	"HU",
	"IE",
	"IT",
	"LV",
	"LT",
	"LU",
	"MT",
	"NL",
	"PL",
	"PT",
	"RO",
	"SK",
	"SI",
	"ES",
	"SE",
	// EEA (non-EU)
	"IS",
	"LI",
	"NO",
	// UK + Crown dependencies under UK GDPR
	"GB",
	"GG",
	"JE",
	"IM",
	"GI",
	// Switzerland (FADP)
	"CH",
]);

/**
 * True when the visitor's country requires prior opt-in consent for analytics.
 * Fails closed: an unknown / empty country requires consent.
 */
export function requiresPriorConsent(
	country: string | null | undefined,
): boolean {
	if (!country) return true;
	return PRIOR_CONSENT_COUNTRIES.has(country.trim().toUpperCase());
}
